import type { ProethicaTestKind } from "@constants/demo/proethica-tests";
import type { TaskCardProps } from "@features/dashboard";
import { useAppTheme } from "@hooks/useAppTheme";
import { Box, ScrollButtonFlatlist, useDevice } from "@memorang/ui";
import { ModuleActionButton } from "components/ModuleActionButton";
import { Pressable } from "react-native";
import { Card, ProgressBar, Text } from "react-native-paper";
import { buttonConfig } from "../../constants";
import { getProethicaModuleInteractionState } from "../../helpers/proethica-module-list";
import { ModuleThumbnail } from "../ModuleThumbnail";

type ModuleRecommendationItem = TaskCardProps & {
	numCompleted?: number;
	numItems?: number;
	hasAccess?: boolean;
	disabled?: boolean;
	testKind?: ProethicaTestKind;
};

type ModuleRecommendationContentProps = {
	item: ModuleRecommendationItem;
	variant: "inline" | "card";
	onPurchasePress?: () => void;
	hasAccess?: boolean;
	isHasAccessLoading?: boolean;
};

const getRecommendationMetadata = (subtitle?: string) => {
	const [typeLabel, durationLabel] = subtitle?.split(/\s*[•·]\s*/) ?? [];

	return {
		typeLabel,
		durationLabel,
	};
};

const getStatusButtonConfig = (status?: string) =>
	buttonConfig[status ?? "NOT_STARTED"] ?? buttonConfig.NOT_STARTED;

export const ModuleRecommendationContent = ({
	item,
	variant,
	onPurchasePress,
	hasAccess = item.hasAccess ?? true,
	isHasAccessLoading,
}: ModuleRecommendationContentProps) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();
	const { buttonMode, buttonIcon } = getStatusButtonConfig(item.status);
	const { needsPurchase, isCurriculumLocked } =
		getProethicaModuleInteractionState(
			hasAccess,
			item.disabled,
			item.status === "COMPLETE",
		);
	const cta = item.cta ?? getStatusButtonConfig(item.status).text ?? "Start";
	const { typeLabel, durationLabel } = getRecommendationMetadata(item.subtitle);
	const progress =
		item.numItems && item.numItems > 0
			? Math.min((item.numCompleted ?? 0) / item.numItems, 1)
			: 0;
	const thumbnailSize = variant === "inline" ? 56 : 44;
	const subtitle = variant === "card" ? typeLabel : item.subtitle;

	const renderThumbnail = () => {
		if (!(item.thumbnailSource || item.testKind)) {
			return null;
		}

		return (
			<ModuleThumbnail
				size={thumbnailSize}
				title={item.title}
				thumbnailSource={item.thumbnailSource}
				locked={isCurriculumLocked}
				isTest={Boolean(item.testKind)}
				testKind={item.testKind}
			/>
		);
	};

	const renderAction = () => {
		if (isHasAccessLoading) {
			return null;
		}

		return (
			<ModuleActionButton
				mode={needsPurchase ? "contained-tonal" : buttonMode}
				icon={needsPurchase ? "lock-outline" : buttonIcon}
				cta={cta}
				onPress={
					needsPurchase && onPurchasePress
						? onPurchasePress
						: item.handleClickTask
				}
				loading={item.loading}
				disabled={needsPurchase ? false : item.loading || isCurriculumLocked}
			/>
		);
	};

	if (variant === "card") {
		return (
			<Box flex={1} gap={14}>
				<Box flexDirection="row" gap={12} alignItems="flex-start">
					{renderThumbnail()}
					<Box flex={1} gap={2} minWidth={0}>
						<Text
							variant="titleSmall"
							style={{
								fontWeight: "700",
								color: theme.colors.onSurface,
								lineHeight: 20,
							}}
							numberOfLines={2}
						>
							{item.title}
						</Text>
						{subtitle ? (
							<Text
								variant="bodySmall"
								style={{ color: theme.colors.onSurfaceVariant }}
								numberOfLines={1}
							>
								{subtitle}
							</Text>
						) : null}
					</Box>
				</Box>
				<ProgressBar
					progress={progress}
					color={theme.colors.primary}
					style={{
						height: 8,
						backgroundColor: theme.colors.surfaceVariant,
					}}
				/>
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
				>
					{durationLabel ? (
						<Text
							variant="bodySmall"
							style={{ color: theme.colors.onSurfaceVariant }}
							numberOfLines={1}
						>
							{durationLabel}
						</Text>
					) : (
						<Box />
					)}
					{renderAction()}
				</Box>
			</Box>
		);
	}

	return (
		<Pressable
			onPress={
				isHasAccessLoading
					? undefined
					: needsPurchase && onPurchasePress
						? onPurchasePress
						: isCurriculumLocked
							? undefined
							: item.handleClickTask
			}
			accessibilityRole="button"
			accessibilityLabel={item.title}
			accessibilityHint={
				needsPurchase
					? "This content requires purchase to access."
					: isCurriculumLocked
						? "Complete earlier modules to unlock this item."
						: "Click to start this item."
			}
			accessibilityState={{ disabled: isCurriculumLocked }}
			style={{
				flex: 1,
			}}
		>
			<Box
				flexDirection={variant === "inline" || isMobile ? "row" : "column"}
				alignItems={variant === "inline" ? "center" : "flex-start"}
				justifyContent="space-between"
				gap={variant === "inline" ? 16 : 12}
				flex={1}
			>
				{renderThumbnail()}
				<Box flex={1} gap={4} minWidth={0}>
					<Text
						variant="titleMedium"
						style={{ fontWeight: "bold", color: theme.colors.onSurface }}
						numberOfLines={2}
					>
						{item.title}
					</Text>
					{subtitle ? (
						<Text
							variant="bodyMedium"
							style={{ color: theme.colors.onSurfaceVariant }}
							numberOfLines={1}
						>
							{subtitle}
						</Text>
					) : null}
				</Box>
				{renderAction()}
			</Box>
		</Pressable>
	);
};

type ModuleRecommendationsSectionProps = {
	items: ModuleRecommendationItem[];
	loading?: boolean;
	onPurchasePress?: () => void;
};

export const ModuleRecommendationsSection = ({
	items,
	onPurchasePress,
}: ModuleRecommendationsSectionProps) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();

	return (
		<ScrollButtonFlatlist
			data={items}
			horizontal
			showsHorizontalScrollIndicator={false}
			keyExtractor={(item) => `${item.title}-${item.status}`}
			contentContainerStyle={{
				paddingHorizontal: isMobile ? 16 : 2,
				gap: 16,
				paddingBottom: 8,
			}}
			renderItem={({ item }) => (
				<Card
					mode="outlined"
					style={{
						width: isMobile ? 320 : 360,
						borderRadius: theme.roundness,
						backgroundColor: theme.colors.surface,
						borderColor: theme.colors.outlineVariant,
					}}
				>
					<Card.Content style={{ padding: 16 }}>
						<ModuleRecommendationContent
							item={item}
							variant="card"
							onPurchasePress={onPurchasePress}
						/>
					</Card.Content>
				</Card>
			)}
		/>
	);
};
